import { ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '~/entities/common/viewer'
import { ViewTransaction } from '~/features/common/Balance/BalanceHistory/ViewTransaction'
import { DriverCreateTransaction } from '~/features/common/Drivers/Balance/CreateTransaction'
import { BalanceHistory } from '~/shared/api'
import { TransactionMoveFundsTypeEnum } from '~/shared/config/enums'
import { canShowObject } from '~/shared/lib/utils'
import { NotFound } from '~/shared/ui/NotFound'
import { Tab, Tabs } from '~/shared/ui/RoutesTabs/type'
import { EditYandexAggregatorPage, ListAggregatorsPage } from './Aggregators'
import { BalancePage } from './Balance'
import {
  CreateRentalContractPage,
  ViewRentalContractPage,
  ListRentalContractPage,
} from './RentalContracts'
import { ListTravelSheetPage } from './TravelSheets'
import { useBreadcrumbs } from './useBreadcrumbs'

type UseConfigTabs = {
  driverId: UniqueId
  detailsPage: ReactNode
}

export const useConfigTabs = ({
  driverId,
  detailsPage,
}: UseConfigTabs): Tabs => {
  const { t } = useTranslation()
  const { canRentalContractsView, canTravelSheetsView, canBalancesView } =
    usePermissions()

  const mountValidateTransaction = useCallback(
    (transaction: BalanceHistory) => {
      if (driverId === transaction.getDriverId()) return null

      return (
        <NotFound
          message={t('alert.uploaded transaction does not match current', {
            transactionId: transaction.getApiId(),
            driverId,
          })}
        />
      )
    },
    [driverId, t],
  )

  const {
    currentViewTransactionBreadcrumbs,
    byDeptViewTransactionBreadcrumbs,
    byBonusViewTransactionBreadcrumbs,
    byDepositViewTransactionBreadcrumbs,
  } = useBreadcrumbs(driverId)

  return [
    {
      labelI18n: 'profile',
      path: '/details',
      content: detailsPage,
    },
    {
      labelI18n: 'aggregators',
      path: '/aggregators',
      content: <ListAggregatorsPage />,
      children: [
        {
          path: '/yandex/:aggregatorId',
          content: <EditYandexAggregatorPage />,
          full: true,
        },
      ],
    },
    ...canShowObject<Tab>(canRentalContractsView, {
      labelI18n: 'contracts',
      path: '/rental-contracts',
      content: <ListRentalContractPage />,
      children: [
        {
          path: '/new',
          content: <CreateRentalContractPage />,
          full: true,
        },
        {
          path: '/:rentalContractId',
          content: <ViewRentalContractPage />,
          full: true,
        },
      ],
    }),
    ...canShowObject<Tab>(canTravelSheetsView, {
      labelI18n: 'trip sheets',
      path: '/travel-sheets',
      content: <ListTravelSheetPage />,
    }),
    ...canShowObject<Tab>(canBalancesView, {
      labelI18n: 'statement',
      path: '/balance',
      content: <BalancePage />,
      children: [
        {
          path: '/current/new-coming',
          content: (
            <DriverCreateTransaction
              moveFundsType={TransactionMoveFundsTypeEnum.COMING}
              breadcrumbsList={currentViewTransactionBreadcrumbs}
            />
          ),
          full: true,
        },
        {
          path: '/current/new-spending',
          content: (
            <DriverCreateTransaction
              moveFundsType={TransactionMoveFundsTypeEnum.SPENDING}
              breadcrumbsList={currentViewTransactionBreadcrumbs}
            />
          ),
          full: true,
        },
        {
          path: '/current/:transactionId',
          content: (
            <ViewTransaction
              parentBreadcrumbs={currentViewTransactionBreadcrumbs}
              mountTransactionValidate={mountValidateTransaction}
            />
          ),
          full: true,
        },
        {
          path: '/by-dept/:transactionId',
          content: (
            <ViewTransaction
              parentBreadcrumbs={byDeptViewTransactionBreadcrumbs}
              mountTransactionValidate={mountValidateTransaction}
            />
          ),
          full: true,
        },
        {
          path: '/by-bonus/:transactionId',
          content: (
            <ViewTransaction
              parentBreadcrumbs={byBonusViewTransactionBreadcrumbs}
              mountTransactionValidate={mountValidateTransaction}
            />
          ),
          full: true,
        },
        {
          path: '/by-deposit/:transactionId',
          content: (
            <ViewTransaction
              parentBreadcrumbs={byDepositViewTransactionBreadcrumbs}
              mountTransactionValidate={mountValidateTransaction}
            />
          ),
          full: true,
        },
      ],
    }),
  ]
}
